import React, { useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { Log, LogData } from '../../../types/log/Log';
import ModalContainer from '../../atoms/Modal/ModalContainer';
import Status from '../../atoms/Status/Status';
import StatusTypes from '../../../types/StatusTypes';
import Button from '../../atoms/Button/Button';
import Flex from '../../atoms/Box/Flex';
import {
    formatDate,
    formatTimeSeconds,
    formatDateISO8601,
    formatTimeMilliSeconds,
} from '../../../services/helpers/dateTimeFormats';
import TwoRowCell from '../../atoms/Table/TwoRowCell';
import Tabs, { TabContent } from '../../../components/atoms/Tabs/Tabs';

interface LogDetailsProps {
    log: Log;
    hide: () => void;
}

interface MessageDetailsProps {
    logData: LogData;
    id: string;
}

const LogDetails: React.FC<LogDetailsProps> = ({ log, hide }: LogDetailsProps) => {
    const [tabState, setTabState] = useState('req');

    const date = formatDate(new Date(log.request.timestamp));
    const time = formatTimeMilliSeconds(new Date(log.request.timestamp));

    const tabsList: TabContent[] = [
        { key: 'req', text: 'Request' },
        { key: 'res', text: 'Response' },
    ];

    const MessageDetails = ({ logData, id }: MessageDetailsProps) => {
        const convertToFormattedJSON = (jsonString: string) => {
            try {
                return JSON.stringify(JSON.parse(jsonString), null, 2);
            } catch (error) {
                return JSON.stringify(null);
            }
        };

        const getISO8601DateAndFractionalSecondsTime = (timestamp: string): string => {
            const getFractionalSecondsFromTimestamp = (timestamp: string) => {
                return timestamp.split('.')[1].split('+')[0];
            };

            return (
                formatDateISO8601(new Date(timestamp)) +
                '\u00A0\u00A0\u00A0' +
                formatTimeSeconds(new Date(timestamp)) +
                '.' +
                getFractionalSecondsFromTimestamp(timestamp)
            );
        };

        const properties = [];
        for (const key in logData.properties) {
            properties.push({ key: key, data: logData.properties[key] });
        }

        return (
            <MessageDetailsOuter>
                <Flex gap="5rem">
                    <Flex gap="1rem" column>
                        <Bold>Timestamp</Bold>
                        <Bold>Id</Bold>
                        {properties.map(property => (
                            <Bold key={property.key}>{property.key}</Bold>
                        ))}
                        <Bold>Payload</Bold>
                    </Flex>
                    <Flex gap="1rem" column>
                        <div>{getISO8601DateAndFractionalSecondsTime(logData.timestamp)}</div>
                        <div>{id}</div>
                        {properties.map((property, ix) => (
                            <div key={ix}>
                                {property.data && property.data.length > 0 ? property.data : '-'}
                            </div>
                        ))}
                        <JsonContainer>{convertToFormattedJSON(logData.payload)}</JsonContainer>
                    </Flex>
                </Flex>
            </MessageDetailsOuter>
        );
    };

    const NoMessage = () => {
        return <div>No response available</div>;
    };

    return (
        <ModalOuter>
            <div>
                <ModalContainer position="header">
                    <Flex justifyContent="space-between">
                        {Number(log.statusCode) !== 0 && (
                            <Status
                                type={StatusTypes.HTTP}
                                status={log.statusCode}
                                height="1.5rem"
                            />
                        )}
                        <Button onClick={hide}>Close</Button>
                    </Flex>
                </ModalContainer>
                <ModalContainerHeaderBottom>
                    <ModalContainer position="content">
                        <Flex gap="5rem">
                            <div>
                                <SmallTitle>Request time</SmallTitle>
                                <TwoRowCell upper={date.yyyyMMdd} lower={time} />
                            </div>
                            <div>
                                <SmallTitle>Method</SmallTitle>
                                <span>{log.httpMethod}</span>
                            </div>
                            <div>
                                <SmallTitle>Path</SmallTitle>
                                <span>{log.requestPath}</span>
                            </div>
                        </Flex>
                    </ModalContainer>
                </ModalContainerHeaderBottom>
            </div>
            <ModalContainerContent>
                <Flex column rowGap={'2rem'}>
                    <Tabs tabsList={tabsList} startTab={tabState} onTabChange={setTabState}>
                        <div key={'req'}>
                            <MessageDetails logData={log.request} id={log.id} />
                        </div>
                        <div key={'res'}>
                            {log.response ? (
                                <MessageDetails logData={log.response} id={log.id} />
                            ) : (
                                <NoMessage />
                            )}
                        </div>
                    </Tabs>
                </Flex>
            </ModalContainerContent>
        </ModalOuter>
    );
};

const MessageDetailsOuter = styled.div`
    max-height: calc(100vh - 40rem);
    overflow-y: scroll;
    color-scheme: ${props => (props.theme.mode === 'dark' ? 'dark' : 'light')};
`;

const ModalOuter = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    background-color: ${props => props.theme.colors.border};
    border-radius: 0.5rem;
    height: fit-content;
`;

const ModalContainerHeaderBottom = styled.div`
    background-color: ${props => props.theme.colors.light};
    border-radius: 0 0 0.5rem 0.5rem;
`;

const ModalContainerContent = styled.div`
    background-color: ${props => props.theme.colors.light};
    border-radius: 0.5rem;
    padding: ${props => props.theme.layout.padding.large};
`;

const SmallTitle = styled.div`
    margin-bottom: 1.5rem;
    font-size: ${props => props.theme.text.size.medium};
`;

const Bold = styled.div`
    font-family: ${props => props.theme.text.font.bold};
    white-space: nowrap;
`;

const JsonContainer = styled.div`
    white-space: pre-wrap;
    font-family: monospace;
    font-size: ${props => props.theme.text.size.xsmall};
    background-color: ${props => props.theme.colors.border};
    padding: 1rem;
    border-radius: 0.5rem;
`;

export default withTheme(LogDetails) as React.ComponentType<Omit<LogDetailsProps, 'theme'>>;
